import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Autocomplete,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    Tooltip,
    Grid
} from "@mui/material";
import {
    CheckCircle as SuccessIcon,
    Warning as WarningIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import { FileUploader } from "react-drag-drop-files";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import PageHeader from "../componenets/shared/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { useMessageContext } from "../contexts/MessageContext";
import {
    parseFile,
    transformToExercises,
    groupExercisesByWorkout,
    extractUniqueExerciseNames,
    FIELD_OPTIONS,
    FIELD_DEFINITIONS
} from "../utils/importTemplateParser";
import { matchExercises, importTemplate } from "../services/TemplatesService";
import { getExercises } from "../services/ExcersiceService";
import { RTL } from "../cache/CacheRtl";
import {
    MAX_BLOCKS,
    MAX_WEEKS_IN_BLOCK,
    MIN_BLOCKS,
    MIN_WEEKS_IN_BLOCK,
    clampPeriodization,
    maxExceededText
} from "../enums/PeriodizationLimits";

const FILE_TYPES = ["CSV", "XLSX", "XLS"];

// Experience level options
const EXPERIENCE_LEVELS = [
    { value: 'BEGINNER', label: 'מתחיל' },
    { value: 'BEGINNER_ADVANCE', label: 'מתחיל-מתקדם' },
    { value: 'MEDIUM', label: 'בינוני' },
    { value: 'ADVANCE', label: 'מתקדם' }
];

// Gender options
const GENDERS = [
    { value: 'MALE', label: 'גבר' },
    { value: 'FEMALE', label: 'אישה' }
];

// Equipment type options
const EQUIPMENT_TYPES = [
    { value: 'GYM', label: 'חדר כושר מלא' },
    { value: 'SMALL_GYM', label: 'חדר כושר קטן' },
    { value: 'STUDIO', label: 'סטודיו' },
    { value: 'HOME_GYM', label: 'אימון ביתי' }
];

// Goals options
const GOALS = [
    { value: 'LOSE_WEIGHT', label: 'הרזייה' },
    { value: 'GAIN_MUSCLE', label: 'בניית שריר' },
    { value: 'HEALTHY_LIFESTYLE', label: 'אורח חיים בריא' },
    { value: 'IMPROVE_FITNESS', label: 'שיפור כושר' }
];

const ImportTemplatePage = () => {
    const { isSuperAdmin } = useAuth();
    const { getCompaniesList } = useCompany();
    const { notificationHandler } = useMessageContext();

    // File and parsing state
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [matching, setMatching] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [columnMappings, setColumnMappings] = useState([]);
    const [fileName, setFileName] = useState("");

    // Parsed exercises state
    const [exercises, setExercises] = useState([]);
    const [workouts, setWorkouts] = useState([]);

    // Exercise matching state
    const [matchResults, setMatchResults] = useState({});
    const [resolvedExercises, setResolvedExercises] = useState({});

    // Exercise resolver dialog state
    const [resolverOpen, setResolverOpen] = useState(false);
    const [resolverExerciseName, setResolverExerciseName] = useState("");
    const [resolverCandidates, setResolverCandidates] = useState([]);
    const [exerciseSearchQuery, setExerciseSearchQuery] = useState("");
    const [allExercises, setAllExercises] = useState([]);
    const [loadingExercises, setLoadingExercises] = useState(false);

    // Template metadata state
    const [templateName, setTemplateName] = useState("");
    const [templateNameHeb, setTemplateNameHeb] = useState("");
    const [templateDescription, setTemplateDescription] = useState("");
    const [templateDescriptionHeb, setTemplateDescriptionHeb] = useState("");
    const [gender, setGender] = useState("");
    const [experienceLevel, setExperienceLevel] = useState("");
    const [bodyPart, setBodyPart] = useState("FULL_BODY");
    const [equipmentType, setEquipmentType] = useState("");
    const [goals, setGoals] = useState([]);
    const [numberOfBlocks, setNumberOfBlocks] = useState(1);
    const [numberOfWeeksInBlock, setNumberOfWeeksInBlock] = useState(4);

    // Whether the last entry into each periodization field was clamped down from an over-limit
    // value. The field would otherwise silently rewrite a typed 40 to 20, which reads as a bug.
    const [blocksClamped, setBlocksClamped] = useState(false);
    const [weeksClamped, setWeeksClamped] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);

    // Summary dialog state
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Load exercises for resolver when dialog opens
    useEffect(() => {
        if (resolverOpen && allExercises.length === 0) {
            loadAllExercises();
        }
    }, [resolverOpen]);

    const loadAllExercises = async () => {
        setLoadingExercises(true);
        try {
            const response = await getExercises(0, 1000);
            setAllExercises(response.data?.content || []);
        } catch (error) {
            console.error("Error loading exercises:", error);
        } finally {
            setLoadingExercises(false);
        }
    };

    const handleFileUpload = async (file) => {
        setLoading(true);
        setFileName(file.name);

        try {
            const result = await parseFile(file);
            setFileData(result);
            setColumnMappings(result.columnMappings);

            // Transform to exercises
            const exerciseList = transformToExercises(result.rawRows, result.columnMappings);
            setExercises(exerciseList);

            // Group by workout
            const workoutList = groupExercisesByWorkout(exerciseList);
            setWorkouts(workoutList);

            // Match exercises
            await matchExerciseNames(exerciseList);

        } catch (error) {
            notificationHandler.error(error.message || "שגיאה בקריאת הקובץ");
            setFileData(null);
            setColumnMappings([]);
        } finally {
            setLoading(false);
        }
    };

    const matchExerciseNames = async (exerciseList) => {
        setMatching(true);
        try {
            const uniqueNames = extractUniqueExerciseNames(exerciseList);
            if (uniqueNames.length === 0) {
                setMatchResults({});
                return;
            }

            const response = await matchExercises(uniqueNames);
            const results = response.data || [];

            // Convert to a map by input name
            const matchMap = {};
            const resolvedMap = {};

            results.forEach(result => {
                matchMap[result.inputName] = result;

                // Auto-resolve if exact or fuzzy match
                if (result.status === 'EXACT' || result.status === 'FUZZY') {
                    resolvedMap[result.inputName] = {
                        exerciseId: result.matchedExerciseId,
                        exerciseName: result.matchedExerciseName,
                        exerciseNameHeb: result.matchedExerciseNameHeb,
                        confidence: result.confidence,
                        status: result.status
                    };
                }
            });

            setMatchResults(matchMap);
            setResolvedExercises(resolvedMap);

        } catch (error) {
            console.error("Error matching exercises:", error);
            notificationHandler.error("שגיאה בהתאמת תרגילים");
        } finally {
            setMatching(false);
        }
    };

    const handleColumnMappingChange = (columnIndex, newField) => {
        const newMappings = columnMappings.map(mapping =>
            mapping.index === columnIndex
                ? { ...mapping, detectedField: newField }
                : mapping
        );
        setColumnMappings(newMappings);

        // Re-transform exercises with new mappings
        if (fileData) {
            const exerciseList = transformToExercises(fileData.rawRows, newMappings);
            setExercises(exerciseList);
            const workoutList = groupExercisesByWorkout(exerciseList);
            setWorkouts(workoutList);

            // Re-match exercises
            matchExerciseNames(exerciseList);
        }
    };

    const handleClearFile = () => {
        setFileData(null);
        setColumnMappings([]);
        setFileName("");
        setExercises([]);
        setWorkouts([]);
        setMatchResults({});
        setResolvedExercises({});
    };

    const openExerciseResolver = (exerciseName) => {
        const match = matchResults[exerciseName];
        setResolverExerciseName(exerciseName);
        setResolverCandidates(match?.candidates || []);
        setExerciseSearchQuery("");
        setResolverOpen(true);
    };

    const handleSelectExercise = (exercise) => {
        setResolvedExercises(prev => ({
            ...prev,
            [resolverExerciseName]: {
                exerciseId: exercise.id,
                exerciseName: exercise.name,
                exerciseNameHeb: exercise.nameHeb,
                confidence: 1.0,
                status: 'MANUAL'
            }
        }));
        setResolverOpen(false);
    };

    const getMatchStatusColor = (exerciseName) => {
        const resolved = resolvedExercises[exerciseName];
        if (resolved) {
            if (resolved.status === 'EXACT') return '#57837E';
            if (resolved.status === 'FUZZY') return '#F59E0B';
            if (resolved.status === 'MANUAL') return '#3B82F6';
        }
        return '#EF4444';
    };

    const getMatchStatusText = (exerciseName) => {
        const resolved = resolvedExercises[exerciseName];
        if (resolved) {
            if (resolved.status === 'EXACT') return 'התאמה מדויקת';
            if (resolved.status === 'FUZZY') return `התאמה חלקית (${Math.round(resolved.confidence * 100)}%)`;
            if (resolved.status === 'MANUAL') return 'נבחר ידנית';
        }
        return 'לא נמצא';
    };

    const getUnresolvedCount = () => {
        const uniqueNames = extractUniqueExerciseNames(exercises);
        return uniqueNames.filter(name => !resolvedExercises[name]).length;
    };

    const handleImport = async () => {
        // Validate required fields
        if (!templateNameHeb.trim()) {
            notificationHandler.error("יש להזין שם תבנית בעברית");
            return;
        }

        if (isSuperAdmin() && !selectedCompany) {
            notificationHandler.error("יש לבחור חברה");
            return;
        }

        // Build the request
        const request = {
            name: templateName || templateNameHeb,
            nameHeb: templateNameHeb,
            description: templateDescription,
            descriptionHeb: templateDescriptionHeb,
            gender: gender || null,
            experienceLevel: experienceLevel || null,
            bodyPart: bodyPart || null,
            equipmentType: equipmentType || null,
            goals: goals.length > 0 ? goals : null,
            numberOfBlocks: numberOfBlocks,
            numberOfWeeksInBlock: numberOfWeeksInBlock,
            companyPermissions: isSuperAdmin() && selectedCompany
                ? [{ companyId: selectedCompany.id }]
                : null,
            workouts: workouts.map(workout => ({
                identifier: workout.identifier,
                name: workout.name,
                nameHeb: workout.nameHeb,
                exercises: workout.exercises.map(exercise => {
                    const resolved = resolvedExercises[exercise.exerciseName];
                    return {
                        exerciseId: resolved?.exerciseId,
                        numberOfSets: exercise.sets,
                        recommendedReps: exercise.reps,
                        minReps: exercise.minReps,
                        maxReps: exercise.maxReps,
                        restTimeInSec: exercise.restTime,
                        durationTime: exercise.duration,
                        tempo: exercise.tempo,
                        typeSet: exercise.typeSet,
                        manipulation: exercise.manipulation,
                        comment: exercise.comment
                    };
                })
            }))
        };

        setImporting(true);
        try {
            const response = await importTemplate(request);
            const result = response.data;

            if (result.success) {
                setImportResult(result);
                setSummaryDialogOpen(true);
            } else {
                notificationHandler.error(result.errorMessage || "שגיאה בייבוא התבנית");
            }
        } catch (error) {
            console.error("Import error:", error);
            notificationHandler.error(error.response?.data?.message || "שגיאה בייבוא התבנית");
        } finally {
            setImporting(false);
        }
    };

    const handleCloseSummaryDialog = () => {
        setSummaryDialogOpen(false);

        if (importResult?.success) {
            // Reset form
            handleClearFile();
            setTemplateName("");
            setTemplateNameHeb("");
            setTemplateDescription("");
            setTemplateDescriptionHeb("");
            setGender("");
            setExperienceLevel("");
            setBodyPart("FULL_BODY");
            setEquipmentType("");
            setGoals([]);
            setNumberOfBlocks(1);
            setNumberOfWeeksInBlock(4);
            setBlocksClamped(false);
            setWeeksClamped(false);
            setSelectedCompany(null);
        }

        setImportResult(null);
    };

    const unresolvedExerciseCount = getUnresolvedCount();

    // Filter exercises for search
    const filteredExercises = allExercises.filter(ex => {
        if (!exerciseSearchQuery.trim()) return true;
        const query = exerciseSearchQuery.toLowerCase();
        return (ex.name?.toLowerCase().includes(query) ||
                ex.nameHeb?.toLowerCase().includes(query));
    });

    return (
        <Box>
            <PageHeader title="ייבוא תבנית תוכנית" />

            <Box sx={{
                paddingRight: { md: "28px", xs: "14px" },
                paddingLeft: { md: "28px", xs: "14px" },
                paddingTop: "20px"
            }}>
                <Typography sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#6B7280",
                    marginBottom: "24px",
                    textAlign: "right"
                }}>
                    העלו קובץ CSV או Excel עם תרגילי התוכנית. המערכת תזהה אוטומטית את התרגילים ותאפשר לכם להתאים אותם.
                </Typography>

                {/* File Upload Area */}
                {!fileData && (
                    <Box sx={{ marginBottom: "24px" }}>
                        <FileUploader
                            handleChange={handleFileUpload}
                            name="file"
                            types={FILE_TYPES}
                            disabled={loading}
                        >
                            <Box sx={{
                                border: "2px dashed #D1D5DB",
                                borderRadius: "12px",
                                padding: "48px 24px",
                                textAlign: "center",
                                cursor: loading ? "not-allowed" : "pointer",
                                backgroundColor: "#FAFBFC",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: "#57837E",
                                    backgroundColor: "#F0F4F3"
                                }
                            }}>
                                {loading ? (
                                    <CircularProgress size={48} sx={{ color: "#57837E" }} />
                                ) : (
                                    <>
                                        <UploadIcon sx={{ fontSize: 48, color: "#9CA3AF", marginBottom: "16px" }} />
                                        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                                            גררו קובץ לכאן או לחצו לבחירה
                                        </Typography>
                                        <Typography sx={{ fontSize: "14px", color: "#9CA3AF" }}>
                                            CSV, XLSX, XLS
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </FileUploader>
                    </Box>
                )}

                {/* File Info & Clear Button */}
                {fileData && (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                        padding: "16px",
                        backgroundColor: "#F0F4F3",
                        borderRadius: "8px"
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                                {fileName}
                            </Typography>
                            <Chip
                                label={`${fileData.totalRows} שורות`}
                                size="small"
                                sx={{ backgroundColor: "#57837E", color: "white" }}
                            />
                            <Chip
                                label={`${workouts.length} אימונים`}
                                size="small"
                                sx={{ backgroundColor: "#3B82F6", color: "white" }}
                            />
                            <Chip
                                label={`${exercises.length} תרגילים`}
                                size="small"
                                sx={{ backgroundColor: "#8B5CF6", color: "white" }}
                            />
                        </Box>
                        <Button
                            onClick={handleClearFile}
                            sx={{
                                color: "#EF4444",
                                fontSize: "14px",
                                textTransform: "none"
                            }}
                        >
                            נקה קובץ
                        </Button>
                    </Box>
                )}

                {/* Column Mapping */}
                {fileData && columnMappings.length > 0 && (
                    <Box sx={{ marginBottom: "24px" }}>
                        <Typography sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1F2937",
                            marginBottom: "16px",
                            textAlign: "right"
                        }}>
                            מיפוי עמודות
                        </Typography>
                        <Paper sx={{
                            padding: "16px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            overflowX: "auto"
                        }}>
                            <Box sx={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "nowrap",
                                minWidth: "fit-content"
                            }}>
                                {columnMappings.map((mapping) => (
                                    <Box key={mapping.index} sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                        minWidth: "140px"
                                    }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                            <Typography sx={{
                                                fontSize: "11px",
                                                color: "#57837E",
                                                fontWeight: 600
                                            }}>
                                                עמודה {mapping.index + 1}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: "12px",
                                                color: mapping.detectedField === 'unknown' ? "#F59E0B"
                                                    : mapping.detectedField === 'ignore' ? "#9CA3AF"
                                                    : "#57837E",
                                                textAlign: "center",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "130px",
                                                fontWeight: mapping.detectedField !== 'unknown' && mapping.detectedField !== 'ignore' ? 600 : 400
                                            }}>
                                                {mapping.detectedField === 'unknown' || mapping.detectedField === 'ignore'
                                                    ? mapping.originalHeader
                                                    : FIELD_DEFINITIONS[mapping.detectedField]?.label || mapping.originalHeader}
                                            </Typography>
                                        </Box>
                                        <RTL>
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={mapping.detectedField}
                                                    onChange={(e) => handleColumnMappingChange(mapping.index, e.target.value)}
                                                    sx={{
                                                        fontSize: "14px",
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: mapping.detectedField === 'unknown' ? "#F59E0B"
                                                                : mapping.detectedField === 'ignore' ? "#D1D5DB"
                                                                : "#57837E"
                                                        }
                                                    }}
                                                >
                                                    {FIELD_OPTIONS.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </RTL>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}

                {/* Exercise Matching Preview */}
                {fileData && workouts.length > 0 && (
                    <Box sx={{ marginBottom: "24px" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                            <Typography sx={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#1F2937",
                                textAlign: "right"
                            }}>
                                תרגילים לפי אימון
                            </Typography>
                            {matching && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <CircularProgress size={16} />
                                    <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                                        מתאים תרגילים...
                                    </Typography>
                                </Box>
                            )}
                            {!matching && unresolvedExerciseCount > 0 && (
                                <Chip
                                    icon={<WarningIcon sx={{ fontSize: 16 }} />}
                                    label={`${unresolvedExerciseCount} תרגילים לא נמצאו`}
                                    size="small"
                                    sx={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
                                />
                            )}
                        </Box>

                        {workouts.map((workout, workoutIndex) => (
                            <Paper key={workoutIndex} sx={{
                                marginBottom: "16px",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                <Box sx={{
                                    padding: "12px 16px",
                                    backgroundColor: "#F8FAFC",
                                    borderBottom: "1px solid #E2E8F0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "16px"
                                }}>
                                    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1F2937" }}>
                                        {workout.nameHeb}
                                    </Typography>
                                    <Chip
                                        label={`${workout.exercises.length} תרגילים`}
                                        size="small"
                                        sx={{ backgroundColor: "#E0E7FF", color: "#3730A3" }}
                                    />
                                </Box>

                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                                                <TableCell align="right" sx={{ fontWeight: 600, width: "40px" }}>#</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>תרגיל (מקובץ)</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, width: "120px" }}>סטטוס</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>תרגיל מותאם</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, width: "60px" }}>סטים</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, width: "60px" }}>חזרות</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, width: "80px" }}>מנוחה</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, width: "60px" }}>פעולה</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {workout.exercises.map((exercise, exerciseIndex) => {
                                                const resolved = resolvedExercises[exercise.exerciseName];
                                                const statusColor = getMatchStatusColor(exercise.exerciseName);
                                                const statusText = getMatchStatusText(exercise.exerciseName);

                                                return (
                                                    <TableRow key={exerciseIndex} sx={{
                                                        "&:hover": { backgroundColor: "#F8FAFC" },
                                                        backgroundColor: !resolved ? "#FEF3C7" : "inherit"
                                                    }}>
                                                        <TableCell align="right" sx={{ color: "#6B7280" }}>
                                                            {exerciseIndex + 1}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                            {exercise.exerciseName}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                size="small"
                                                                label={statusText}
                                                                sx={{
                                                                    backgroundColor: `${statusColor}20`,
                                                                    color: statusColor,
                                                                    fontSize: "11px"
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: resolved ? "#374151" : "#9CA3AF" }}>
                                                            {resolved?.exerciseNameHeb || "—"}
                                                        </TableCell>
                                                        <TableCell align="center">{exercise.sets || "—"}</TableCell>
                                                        <TableCell align="center">{exercise.reps || "—"}</TableCell>
                                                        <TableCell align="center">{exercise.restTime ? `${exercise.restTime}s` : "—"}</TableCell>
                                                        <TableCell align="center">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openExerciseResolver(exercise.exerciseName)}
                                                                sx={{ color: resolved ? "#6B7280" : "#F59E0B" }}
                                                            >
                                                                {resolved ? <EditIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Template Metadata Form */}
                {fileData && workouts.length > 0 && (
                    <Paper sx={{
                        padding: "24px",
                        marginBottom: "24px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px"
                    }}>
                        <Typography sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1F2937",
                            marginBottom: "20px",
                            textAlign: "right"
                        }}>
                            פרטי התבנית
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        label="שם התבנית (עברית) *"
                                        value={templateNameHeb}
                                        onChange={(e) => setTemplateNameHeb(e.target.value)}
                                        size="small"
                                    />
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        label="שם התבנית (אנגלית)"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        size="small"
                                    />
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RTL>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>מין</InputLabel>
                                        <Select
                                            value={gender}
                                            onChange={(e) => setGender(e.target.value)}
                                            label="מין"
                                        >
                                            <MenuItem value="">לא נבחר</MenuItem>
                                            {GENDERS.map(g => (
                                                <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RTL>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>רמת ניסיון</InputLabel>
                                        <Select
                                            value={experienceLevel}
                                            onChange={(e) => setExperienceLevel(e.target.value)}
                                            label="רמת ניסיון"
                                        >
                                            <MenuItem value="">לא נבחר</MenuItem>
                                            {EXPERIENCE_LEVELS.map(e => (
                                                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RTL>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>סוג ציוד</InputLabel>
                                        <Select
                                            value={equipmentType}
                                            onChange={(e) => setEquipmentType(e.target.value)}
                                            label="סוג ציוד"
                                        >
                                            <MenuItem value="">לא נבחר</MenuItem>
                                            {EQUIPMENT_TYPES.map(e => (
                                                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RTL>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>מטרות</InputLabel>
                                        <Select
                                            multiple
                                            value={goals}
                                            onChange={(e) => setGoals(e.target.value)}
                                            label="מטרות"
                                            renderValue={(selected) =>
                                                selected.map(v => GOALS.find(g => g.value === v)?.label).join(', ')
                                            }
                                        >
                                            {GOALS.map(g => (
                                                <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        label="מספר בלוקים"
                                        type="number"
                                        value={numberOfBlocks}
                                        onChange={(e) => {
                                            const typed = parseInt(e.target.value, 10);
                                            setBlocksClamped(!Number.isNaN(typed) && typed > MAX_BLOCKS);
                                            setNumberOfBlocks(
                                                clampPeriodization(e.target.value, MAX_BLOCKS, MIN_BLOCKS));
                                        }}
                                        size="small"
                                        inputProps={{ min: MIN_BLOCKS, max: MAX_BLOCKS }}
                                        error={blocksClamped}
                                        helperText={blocksClamped ? maxExceededText(MAX_BLOCKS) : ''}
                                    />
                                </RTL>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        label="שבועות בבלוק"
                                        type="number"
                                        value={numberOfWeeksInBlock}
                                        onChange={(e) => {
                                            const typed = parseInt(e.target.value, 10);
                                            setWeeksClamped(!Number.isNaN(typed) && typed > MAX_WEEKS_IN_BLOCK);
                                            setNumberOfWeeksInBlock(clampPeriodization(
                                                e.target.value, MAX_WEEKS_IN_BLOCK, MIN_WEEKS_IN_BLOCK));
                                        }}
                                        size="small"
                                        inputProps={{ min: MIN_WEEKS_IN_BLOCK, max: MAX_WEEKS_IN_BLOCK }}
                                        error={weeksClamped}
                                        helperText={weeksClamped ? maxExceededText(MAX_WEEKS_IN_BLOCK) : ''}
                                    />
                                </RTL>
                            </Grid>

                            {isSuperAdmin() && (
                                <Grid item xs={12} md={6}>
                                    <RTL>
                                        <Autocomplete
                                            options={getCompaniesList()}
                                            getOptionLabel={(option) => option.name || ''}
                                            value={selectedCompany}
                                            onChange={(event, newValue) => setSelectedCompany(newValue)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="חברה *"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </RTL>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                )}

                {/* Action Bar */}
                {fileData && workouts.length > 0 && (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "20px 24px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        marginBottom: "24px"
                    }}>
                        <Box>
                            {unresolvedExerciseCount > 0 && (
                                <Typography sx={{ fontSize: "14px", color: "#EF4444" }}>
                                    יש לפתור {unresolvedExerciseCount} תרגילים שלא נמצאו לפני הייבוא
                                </Typography>
                            )}
                        </Box>
                        <Tooltip title={unresolvedExerciseCount > 0 ? "יש לפתור את כל התרגילים שלא נמצאו" : ""}>
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleImport}
                                    disabled={
                                        importing ||
                                        unresolvedExerciseCount > 0 ||
                                        !templateNameHeb.trim() ||
                                        (isSuperAdmin() && !selectedCompany)
                                    }
                                    sx={{
                                        backgroundColor: "#57837E",
                                        color: "white",
                                        fontWeight: 600,
                                        height: "44px",
                                        minWidth: "140px",
                                        fontSize: "14px",
                                        padding: "10px 24px",
                                        textTransform: "none",
                                        "&:hover": { backgroundColor: "#466963" },
                                        "&:disabled": {
                                            backgroundColor: "#D1D5DB",
                                            color: "#9CA3AF"
                                        }
                                    }}
                                >
                                    {importing ? <CircularProgress size={24} sx={{ color: "white" }} /> : "ייבא תבנית"}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Exercise Resolver Dialog */}
            <Dialog
                open={resolverOpen}
                onClose={() => setResolverOpen(false)}
                maxWidth="sm"
                fullWidth
                dir="rtl"
            >
                <DialogTitle sx={{
                    backgroundColor: "#57837E",
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Typography sx={{ fontWeight: 600 }}>
                        בחר תרגיל עבור: "{resolverExerciseName}"
                    </Typography>
                    <IconButton onClick={() => setResolverOpen(false)} sx={{ color: "white" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: "24px" }}>
                    <RTL>
                        <TextField
                            fullWidth
                            placeholder="חפש תרגיל..."
                            value={exerciseSearchQuery}
                            onChange={(e) => setExerciseSearchQuery(e.target.value)}
                            size="small"
                            sx={{ marginBottom: "16px", marginTop: "16px" }}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ color: "#9CA3AF", marginLeft: "8px" }} />
                            }}
                        />
                    </RTL>

                    {resolverCandidates.length > 0 && !exerciseSearchQuery && (
                        <>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                                הצעות התאמה:
                            </Typography>
                            <List sx={{ backgroundColor: "#F8FAFC", borderRadius: "8px", marginBottom: "16px" }}>
                                {resolverCandidates.map((candidate, index) => (
                                    <ListItem
                                        key={index}
                                        button
                                        onClick={() => handleSelectExercise({
                                            id: candidate.exerciseId,
                                            name: candidate.name,
                                            nameHeb: candidate.nameHeb
                                        })}
                                        sx={{
                                            borderBottom: index < resolverCandidates.length - 1 ? "1px solid #E5E7EB" : "none",
                                            "&:hover": { backgroundColor: "#E0E7FF" }
                                        }}
                                    >
                                        <ListItemText
                                            primary={candidate.nameHeb || candidate.name}
                                            secondary={`התאמה: ${Math.round(candidate.score * 100)}%`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                            <Divider sx={{ marginBottom: "16px" }} />
                        </>
                    )}

                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                        כל התרגילים:
                    </Typography>

                    {loadingExercises ? (
                        <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <List sx={{
                            maxHeight: "300px",
                            overflow: "auto",
                            backgroundColor: "#FAFAFA",
                            borderRadius: "8px"
                        }}>
                            {filteredExercises.slice(0, 50).map((exercise, index) => (
                                <ListItem
                                    key={exercise.id}
                                    button
                                    onClick={() => handleSelectExercise(exercise)}
                                    sx={{
                                        borderBottom: index < Math.min(filteredExercises.length, 50) - 1 ? "1px solid #E5E7EB" : "none",
                                        "&:hover": { backgroundColor: "#E0E7FF" }
                                    }}
                                >
                                    <ListItemText
                                        primary={exercise.nameHeb || exercise.name}
                                        secondary={exercise.name !== exercise.nameHeb ? exercise.name : null}
                                    />
                                </ListItem>
                            ))}
                            {filteredExercises.length > 50 && (
                                <ListItem>
                                    <ListItemText
                                        primary={`... ועוד ${filteredExercises.length - 50} תרגילים`}
                                        sx={{ color: "#6B7280", textAlign: "center" }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    )}
                </DialogContent>
            </Dialog>

            {/* Import Summary Dialog */}
            <Dialog
                open={summaryDialogOpen}
                onClose={handleCloseSummaryDialog}
                maxWidth="sm"
                fullWidth
                dir="rtl"
            >
                <DialogTitle sx={{
                    backgroundColor: "#57837E",
                    color: "white",
                    fontWeight: 600
                }}>
                    ייבוא הושלם בהצלחה
                </DialogTitle>
                <DialogContent sx={{ padding: "24px" }}>
                    {importResult && (
                        <Box sx={{ marginTop: "16px" }}>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "16px",
                                backgroundColor: "#E8F5E9",
                                borderRadius: "8px",
                                marginBottom: "16px"
                            }}>
                                <SuccessIcon sx={{ color: "#2E7D32", fontSize: 32 }} />
                                <Typography sx={{ fontSize: "16px", color: "#2E7D32" }}>
                                    התבנית "{importResult.templateNameHeb}" נוצרה בהצלחה!
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", gap: "16px" }}>
                                <Box sx={{
                                    flex: 1,
                                    padding: "16px",
                                    backgroundColor: "#E3F2FD",
                                    borderRadius: "8px",
                                    textAlign: "center"
                                }}>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#1565C0" }}>
                                        {importResult.workoutsCreated}
                                    </Typography>
                                    <Typography sx={{ fontSize: "14px", color: "#1565C0" }}>
                                        אימונים
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    flex: 1,
                                    padding: "16px",
                                    backgroundColor: "#F3E5F5",
                                    borderRadius: "8px",
                                    textAlign: "center"
                                }}>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#7B1FA2" }}>
                                        {importResult.exercisesCreated}
                                    </Typography>
                                    <Typography sx={{ fontSize: "14px", color: "#7B1FA2" }}>
                                        תרגילים
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button
                        onClick={handleCloseSummaryDialog}
                        variant="contained"
                        sx={{
                            backgroundColor: "#57837E",
                            "&:hover": { backgroundColor: "#466963" }
                        }}
                    >
                        סגור
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImportTemplatePage;
